import { Search, ChevronsUpDown, Check } from "lucide-react";
import { useState } from "react";

import { Button } from "@/app/_module/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/app/_module/components/ui/command";
import { Input } from "@/app/_module/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/app/_module/components/ui/popover";
import { FilterPop } from "@/app/_module/components/ui/shell-popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import { cn } from "@/app/_module/lib/utils";

import type { ResourceFilters as ResourceFiltersType, ResourceCategory, ResourceScope } from "../types/types";

interface Carrier {
  id: string;
  name: string;
}

interface AgencyOption {
  id: string;
  name: string;
  depth?: number;
}

interface ResourceFiltersProps {
  filters: ResourceFiltersType;
  onFiltersChange: (filters: Partial<ResourceFiltersType>) => void;
  carriers: Carrier[];
  categories: ResourceCategory[];
  showScopeFilter?: boolean;
  agencies?: AgencyOption[];
  showAgencyFilter?: boolean;
}

export const ResourceFilters = function({
  filters,
  onFiltersChange,
  carriers,
  categories,
  showScopeFilter = false,
  agencies = [],
  showAgencyFilter = false,
}: ResourceFiltersProps) {
  const [carrierOpen, setCarrierOpen] = useState(false);

  return (
    <div className={`grid grid-cols-1 gap-4 ${showScopeFilter && showAgencyFilter ? 'md:grid-cols-6' : showScopeFilter || showAgencyFilter ? 'md:grid-cols-5' : 'md:grid-cols-4'}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          className="pl-10"
          onChange={(e) => onFiltersChange({ search: e.target.value || undefined })}
          placeholder="Search documents..."
          value={filters.search || ""}
        />
      </div>

      <Select
        onValueChange={(value) => onFiltersChange({ category: value === "all" ? undefined : value })}
        value={filters.category || "all"}
      >
        <SelectTrigger>
          <SelectValue placeholder="All Categories" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Categories</SelectItem>
          {categories.map((category) => (
            <SelectItem key={category.id} value={category.id}>
              {category.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <FilterPop
        open={carrierOpen}
        onOpenChange={setCarrierOpen}
        className="w-[--radix-popover-trigger-width] p-0"
        trigger={
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={carrierOpen}
            className="w-full justify-between font-normal"
          >
            {filters.carrier
              ? carriers.find((c) => c.id === filters.carrier)?.name || filters.carrier
              : "All Carriers"}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        }
      >
          <Command>
            <CommandInput placeholder="Search carriers..." />
            <CommandList className="max-h-[200px]">
              <CommandEmpty>No carriers found.</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  value="all-carriers"
                  onSelect={() => {
                    onFiltersChange({ carrier: undefined });
                    setCarrierOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      !filters.carrier ? "opacity-100" : "opacity-0"
                    )}
                  />
                  All Carriers
                </CommandItem>
                {carriers.map((carrier) => (
                  <CommandItem
                    key={carrier.id}
                    value={carrier.name}
                    onSelect={() => {
                      onFiltersChange({ carrier: carrier.id });
                      setCarrierOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        filters.carrier === carrier.id ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {carrier.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
      </FilterPop>

      <Select
        onValueChange={(value) => onFiltersChange({ fileType: value === "all" ? undefined : value })}
        value={filters.fileType || "all"}
      >
        <SelectTrigger>
          <SelectValue placeholder="All File Types" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All File Types</SelectItem>
          {/* Documents */}
          <SelectItem value="PDF">PDF</SelectItem>
          <SelectItem value="DOC">DOC</SelectItem>
          <SelectItem value="DOCX">DOCX</SelectItem>
          <SelectItem value="TXT">TXT</SelectItem>
          <SelectItem value="RTF">RTF</SelectItem>
          {/* Spreadsheets */}
          <SelectItem value="XLS">XLS</SelectItem>
          <SelectItem value="XLSX">XLSX</SelectItem>
          <SelectItem value="CSV">CSV</SelectItem>
          <SelectItem value="ODS">ODS</SelectItem>
          {/* Presentations */}
          <SelectItem value="PPT">PPT</SelectItem>
          <SelectItem value="PPTX">PPTX</SelectItem>
          <SelectItem value="ODP">ODP</SelectItem>
          {/* OpenDocument text */}
          <SelectItem value="ODT">ODT</SelectItem>
          {/* Images */}
          <SelectItem value="JPG">JPG</SelectItem>
          <SelectItem value="JPEG">JPEG</SelectItem>
          <SelectItem value="PNG">PNG</SelectItem>
          <SelectItem value="GIF">GIF</SelectItem>
          <SelectItem value="WEBP">WEBP</SelectItem>
          <SelectItem value="SVG">SVG</SelectItem>
          {/* Video */}
          <SelectItem value="MP4">MP4</SelectItem>
          <SelectItem value="WEBM">WEBM</SelectItem>
          <SelectItem value="MOV">MOV</SelectItem>
          {/* Audio */}
          <SelectItem value="MP3">MP3</SelectItem>
          <SelectItem value="WAV">WAV</SelectItem>
          <SelectItem value="M4A">M4A</SelectItem>
          {/* Archives */}
          <SelectItem value="ZIP">ZIP</SelectItem>
          {/* eBooks */}
          <SelectItem value="EPUB">EPUB</SelectItem>
        </SelectContent>
      </Select>

      {/* Visibility Scope Filter - Only show for agency users */}
      {showScopeFilter && (
        <Select
          onValueChange={(value) => onFiltersChange({ scope: value === "all" ? undefined : value as ResourceScope })}
          value={filters.scope || "all"}
        >
          <SelectTrigger>
            <SelectValue placeholder="All Visibility" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Visibility</SelectItem>
            <SelectItem value="personal">Personal</SelectItem>
            <SelectItem value="agency-only">My Agency</SelectItem>
            <SelectItem value="agency-and-below">Agency + Subs</SelectItem>
            <SelectItem value="agency-wide">Agency-Wide</SelectItem>
          </SelectContent>
        </Select>
      )}

      {showAgencyFilter && agencies.length > 0 && (
        <FilterPop
          className="w-[280px] p-0"
          trigger={
            <Button
              variant="outline"
              className="w-full justify-between font-normal"
            >
              <span className="flex items-center gap-2 truncate">
                {filters.agencyId ? (
                  <>
                    <span className="w-5 h-5 rounded-md bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0">
                      {agencies.find(a => a.id === filters.agencyId)?.name?.charAt(0) || '?'}
                    </span>
                    <span className="truncate">{agencies.find(a => a.id === filters.agencyId)?.name}</span>
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground flex-shrink-0"><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/><path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/></svg>
                    <span>All Agencies</span>
                  </>
                )}
              </span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          }
        >
            <div className="px-3 py-2.5 border-b bg-muted/30">
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Agency Network</p>
            </div>
            <div className="py-1 max-h-[300px] overflow-y-auto">
              <button
                onClick={() => onFiltersChange({ agencyId: undefined })}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors text-sm ${
                  !filters.agencyId ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted/50'
                }`}
              >
                <div className="w-6 h-6 rounded-lg bg-muted flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/></svg>
                </div>
                <span>All Agencies</span>
                {!filters.agencyId && <Check className="w-4 h-4 ml-auto text-primary" />}
              </button>

              {agencies.map((a) => {
                const isSelected = filters.agencyId === a.id;
                const isRoot = !a.depth || a.depth === 0;
                const depth = a.depth || 0;
                const depthColors = [
                  { bg: 'bg-gradient-to-br from-blue-500 to-indigo-600', text: 'text-white', bar: 'bg-blue-400' },
                  { bg: 'bg-gradient-to-br from-violet-500 to-purple-600', text: 'text-white', bar: 'bg-violet-400' },
                  { bg: 'bg-gradient-to-br from-emerald-500 to-teal-600', text: 'text-white', bar: 'bg-emerald-400' },
                  { bg: 'bg-gradient-to-br from-amber-500 to-orange-600', text: 'text-white', bar: 'bg-amber-400' },
                  { bg: 'bg-gradient-to-br from-rose-500 to-pink-600', text: 'text-white', bar: 'bg-rose-400' },
                ];
                const color = depthColors[depth % depthColors.length];
                return (
                  <button
                    key={a.id}
                    onClick={() => onFiltersChange({ agencyId: a.id })}
                    className={`w-full flex items-center gap-2.5 py-2 text-left transition-all text-sm ${
                      isSelected ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted/50'
                    }`}
                    style={{ paddingLeft: `${12 + depth * 16}px`, paddingRight: '12px' }}
                  >
                    {depth > 0 && (
                      <div className="flex items-center -ml-1 mr-0.5">
                        <div className={`w-3 h-px ${color.bar}`} />
                      </div>
                    )}
                    <div className={`w-6 h-6 rounded-lg flex items-center justify-center text-[10px] font-bold flex-shrink-0 shadow-sm ${
                      isSelected ? 'ring-2 ring-primary ring-offset-1' : ''
                    } ${color.bg} ${color.text}`}>
                      {a.name?.charAt(0) || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="truncate font-medium">{a.name}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {isRoot ? 'Your agency' : depth === 1 ? 'Sub-agency' : `${'Sub-'.repeat(depth - 1)}sub-agency`}
                      </p>
                    </div>
                    {isSelected && <Check className="w-4 h-4 ml-auto text-primary flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
        </FilterPop>
      )}
    </div>
  );
};